import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getDashboardAuctionItemsSelector, getDashboardAuctionItems} from '@/store/organization-dashboard';
//mui
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from '@mui/material';
//constants
import {
	AUCTION,
	BID_INCREMENT,
	BID_INCREMENT_CAMEL,
	BODY1,
	BUY_NOW_PRICE,
	BUY_NOW_PRICE_CAMEL,
	CAPTION,
	DOLLAR,
	DOWNLOAD_CSV,
	ID,
	IMAGE_TABLE_ALT,
	ITEM,
	MIN_BID,
	MIN_BID_CAMEL,
	RIGHT,
	STATUS,
	SUBTITLE1,
	VALUE,
	defaultPage,
	defaultPageSize,
} from '@/utils/constants';
//helpers
import {downloadFile, getErrorMessage, getTableStatusStyle} from '@/utils/helpers';
//icons
import {ReactComponent as UpDownIcon} from '@/assets/icons/arrow-up-down.svg';
import {ReactComponent as ImportFileIcon} from '@/assets/icons/import-file.svg';
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
import {ReactComponent as JudgeIcon} from '@icons/sidebar/judge.svg';
//services
import {OrganizationDashboardService} from '@/utils/services';
//enums
import {STATISTIC} from '@/utils/enums';
//components
import Loader from '@/shared/components/Loader';
import SearchHeader from '@/shared/components/Table/SearchHeader';
import Button from '@/shared/components/Button';
import Pagination from '@/shared/components/Pagination';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
//styles
import {statisticAuctionItemsStyle} from './StatisticAuctionItems.styles';

const StatisticAuctionItems = () => {
	const dispatch = useAppDispatch();

	const [currentPage, setCurrentPage] = useState(defaultPage);
	const [isDataLoaded, setIsDataLoaded] = useState(false);
	const [searchName, setSearchName] = useState('');

	const [sortColumn, setSortColumn] = useState<string>('');
	const [isOrderDesc, setIsOrderDesc] = useState<boolean>();

	const currentEvent = useAppSelector(getCurrentEventSelector);
	const auctionItems = useAppSelector(getDashboardAuctionItemsSelector);

	const {data, pageSize, totalCount} = auctionItems;

	useEffect(() => {
		if (currentEvent) getAuctionItems(searchName);
	}, [currentEvent, currentPage, sortColumn, isOrderDesc]);

	const handleSortChange = (newSortColumn: string) => () => {
		const isAsc = sortColumn !== newSortColumn || !isOrderDesc;
		setIsOrderDesc(isAsc);
		setSortColumn(newSortColumn);
	};

	const getAuctionItems = async (value?: string) => {
		setIsDataLoaded(false);

		try {
			const reqBody = {
				eventId: currentEvent!.id,
				page: currentPage,
				sortColumn,
				isOrderDesc,
				searchName: value,
			};

			await dispatch(getDashboardAuctionItems(reqBody)).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsDataLoaded(true);
		}
	};

	const handleDownloadSvc = async () => {
		if (currentEvent) {
			const file = await OrganizationDashboardService.getDashboardAuctionItemsCsv(currentEvent.id);
			downloadFile(file.data, STATISTIC.AUCTION_ITEMS);
		}
	};

	const handlePaginationChange = (step: number) => {
		if (isOrderDesc) setIsOrderDesc(false);
		if (sortColumn?.length) setSortColumn('');

		setCurrentPage(step);
	};

	const handleSearchChange = (value: string) => {
		setCurrentPage(defaultPage);
		setSearchName(value);
	};

	const handlePressEnter = () => {
		setCurrentPage(defaultPage);

	};

	const getPayStatusSx = (payStatus: string) => getTableStatusStyle(payStatus);

	useEffect(() => {
	if (currentEvent) getAuctionItems(searchName);
}, [currentEvent, currentPage, sortColumn, isOrderDesc, searchName]);

	return (
		<Box sx={statisticAuctionItemsStyle.wrapper}>
			<SearchHeader
				page={currentPage}
				items={totalCount < defaultPageSize ? totalCount : defaultPageSize}
				total={totalCount}
				onPressEnter={handlePressEnter}
				onSearchChange={handleSearchChange}
				actionButton={
					<Button
						onClick={handleDownloadSvc}
						title={DOWNLOAD_CSV}
						disabled={!totalCount}
						startIcon={<ImportFileIcon />}
						sx={statisticAuctionItemsStyle.searchButton}
					/>
				}
			/>

			{isDataLoaded ? (
				totalCount ? (
					<Box sx={statisticAuctionItemsStyle.tableContent}>
						<TableContainer sx={statisticAuctionItemsStyle.container}>
							<Table sx={statisticAuctionItemsStyle.table} stickyHeader>
								<TableHead>
									<TableRow sx={statisticAuctionItemsStyle.tableHeader}>
										<TableCell>
											<TableSortLabel active onClick={handleSortChange(ID)} IconComponent={UpDownIcon}>
												{ID}
											</TableSortLabel>
										</TableCell>

										<TableCell>{ITEM}</TableCell>

										<TableCell>{AUCTION}</TableCell>

										<TableCell align={RIGHT}>
											<TableSortLabel active onClick={handleSortChange(VALUE)} IconComponent={UpDownIcon}>
												{VALUE}
											</TableSortLabel>
										</TableCell>

										<TableCell align={RIGHT}>
											<TableSortLabel active onClick={handleSortChange(MIN_BID_CAMEL)} IconComponent={UpDownIcon}>
												{MIN_BID}
											</TableSortLabel>
										</TableCell>

										<TableCell align={RIGHT}>
											<Box sx={statisticAuctionItemsStyle.headerCell}>
												<TableSortLabel
													active
													onClick={handleSortChange(BID_INCREMENT_CAMEL)}
													IconComponent={UpDownIcon}
												>
													<Typography variant={CAPTION} sx={statisticAuctionItemsStyle.headerCellTitle}>
														{BID_INCREMENT}
													</Typography>
												</TableSortLabel>
											</Box>
										</TableCell>

										<TableCell align={RIGHT}>
											<Box sx={statisticAuctionItemsStyle.headerCell}>
												<TableSortLabel
													active
													onClick={handleSortChange(BUY_NOW_PRICE_CAMEL)}
													IconComponent={UpDownIcon}
												>
													<Typography variant={CAPTION} sx={statisticAuctionItemsStyle.headerCellTitle}>
														{BUY_NOW_PRICE}
													</Typography>
												</TableSortLabel>
											</Box>
										</TableCell>

										<TableCell align={RIGHT}>{STATUS}</TableCell>
									</TableRow>
								</TableHead>

								<TableBody>
									{data.map((row) => (
										<TableRow key={row.id} sx={statisticAuctionItemsStyle.tableBody}>
											<TableCell>{row.id}</TableCell>

											<TableCell>
												<Box sx={statisticAuctionItemsStyle.bodyCell}>
													<Box sx={statisticAuctionItemsStyle.bodyCellImage}>
														{row.photo ? <img src={row.photo} alt={IMAGE_TABLE_ALT} /> : <JudgeIcon />}
														{/* <img src={row.photo ?? TestImage} alt={IMAGE_TABLE_ALT} /> */}
													</Box>
													<Box sx={statisticAuctionItemsStyle.bodyCellImageContent}>
														<Typography variant={SUBTITLE1} sx={statisticAuctionItemsStyle.bodyCellImageContentName}>
															{row.name}
														</Typography>
														<Typography variant={BODY1} sx={statisticAuctionItemsStyle.bodyCellImageContentCategory}>
															{row.categoryName}
														</Typography>
													</Box>
												</Box>
											</TableCell>

											<TableCell>{row.auctionType}</TableCell>

											<TableCell align={RIGHT}>
												{DOLLAR}
												{row.value}
											</TableCell>

											<TableCell align={RIGHT}>
												{DOLLAR}
												{row.minBid}
											</TableCell>

											<TableCell align={RIGHT}>
												{DOLLAR}
												{row.bidIncrement}
											</TableCell>

											<TableCell align={RIGHT}>
												{DOLLAR}
												{row.buyNowPrice}
											</TableCell>

											<TableCell align={RIGHT}>
												<Typography sx={getPayStatusSx(row.status)}>
													{row.status}
													<EllipseIcon />
												</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<Box sx={statisticAuctionItemsStyle.pagination}>
							<Pagination
								page={currentPage}
								pageSize={pageSize}
								total={totalCount}
								onChange={(step) => handlePaginationChange(step)}
							/>
						</Box>
					</Box>
				) : (
					<EmptyTableData />
				)
			) : (
				<Loader parentClassName={statisticAuctionItemsStyle.loader} size={50} />
			)}
		</Box>
	);
};

export default StatisticAuctionItems;
